import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Sidebar from '@/components/feature/Sidebar';

interface Notification {
  id: number;
  recipient_uid: string;
  sender_uid: string | null;
  type: string;
  title: string;
  message: string;
  reference_type: string | null;
  reference_id: number | null;
  case_number: string | null;
  is_read: number;
  created_at: string;
}

export default function SWNotificationsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchNotifications = async () => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    try {
      setError('');

      const response = await fetch(
        `https://guardiancloudcare.com/backend/api/get_sw_notifications.php?uid=${encodeURIComponent(user.uid)}`
      );

      if (!response.ok) {
        throw new Error(
          `Request failed with status ${response.status}`
        );
      }

      const data = await response.json();

      if (!data?.success) {
        throw new Error(
          data?.message || 'Unable to load notifications.'
        );
      }

      const notificationList = Array.isArray(data.notifications)
        ? data.notifications
        : Array.isArray(data.data?.notifications)
          ? data.data.notifications
          : [];

      setNotifications(notificationList);

      setNotifications(
        Array.isArray(data.notifications)
          ? data.notifications
          : []
      );
    } catch (err: any) {
      console.error(
        'Social Worker notifications error:',
        err
      );

      setError(
        err?.message ||
        'Unable to load notifications.'
      );
    } finally {
      setLoading(false);
    }
  };

      const markAsRead = async (notificationId: number) => {
    if (!user?.uid) {
      return;
    }

    try {
      const response = await fetch(
        'https://guardiancloudcare.com/backend/api/mark_sw_notification_read.php',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            uid: user.uid,
            notification_id: notificationId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Mark as read request failed: ${response.status}`
        );
      }

      const data = await response.json();

      if (!data?.success) {
        throw new Error(
          data?.message ||
          'Unable to mark notification as read.'
        );
      }

      setNotifications(current =>
        current.map(notification =>
          notification.id === notificationId
            ? {
                ...notification,
                is_read: 1,
              }
            : notification
        )
      );
    } catch (error) {
      console.error(
        'Mark notification as read error:',
        error
      );
    }
  };

  const markAllAsRead = async () => {
    if (!user?.uid) {
      return;
    }

    try {
      const response = await fetch(
        'https://guardiancloudcare.com/backend/api/mark_all_sw_notifications_read.php',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            uid: user.uid,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Mark all as read request failed: ${response.status}`
        );
      }

      const data = await response.json();

      if (!data?.success) {
        throw new Error(
          data?.message ||
          'Unable to mark all notifications as read.'
        );
      }

      setNotifications(current =>
        current.map(notification => ({
          ...notification,
          is_read: 1,
        }))
      );
    } catch (error) {
      console.error(
        'Mark all notifications as read error:',
        error
      );
    }
  };

  
  useEffect(() => {
    fetchNotifications();

    const interval = setInterval(
      fetchNotifications,
      5000
    );

    return () => clearInterval(interval);
  }, [user?.uid]);

  const unreadCount = notifications.filter(
    notification =>
      Number(notification.is_read) === 0
  ).length;

  const formatDate = (value: string) => {
    if (!value) {
      return '';
    }

    const date = new Date(
      value.replace(' ', 'T')
    );

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString(
      undefined,
      {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }
    );
  };

  const getNotificationIcon = (
    type: string
  ) => {
    switch (type) {
      case 'urgent_case':
        return 'ri-alarm-warning-fill';

      case 'victim_report_submitted':
        return 'ri-file-warning-line';

      case 'schedule_created':
        return 'ri-calendar-check-line';

      case 'schedule_updated':
        return 'ri-calendar-edit-line';

      case 'schedule_cancelled':
        return 'ri-calendar-close-line';

      case 'case_update':
        return 'ri-folder-info-line';

      case 'message':
        return 'ri-message-3-line';

      default:
        return 'ri-notification-3-line';
    }
  };

  const getNotificationColor = (
    type: string
  ) => {
    switch (type) {
      case 'urgent_case':
        return 'bg-red-100 text-red-600';

      case 'victim_report_submitted':
        return 'bg-orange-100 text-orange-600';

      case 'schedule_created':
        return 'bg-teal-100 text-teal-600';

      case 'schedule_updated':
        return 'bg-blue-100 text-blue-600';

      case 'schedule_cancelled':
        return 'bg-red-100 text-red-600';

      case 'case_update':
        return 'bg-purple-100 text-purple-600';

      case 'message':
        return 'bg-indigo-100 text-indigo-600';

      default:
        return 'bg-slate-100 text-slate-600';
    }
  };

  const getNotificationLabel = (
    type: string
  ) => {
    switch (type) {
      case 'urgent_case':
        return 'Urgent Case';

      case 'victim_report_submitted':
        return 'New Victim Report';

      case 'schedule_created':
        return 'Schedule Created';

      case 'schedule_updated':
        return 'Schedule Updated';

      case 'schedule_cancelled':
        return 'Schedule Cancelled';

      case 'case_update':
        return 'Case Update';

      case 'message':
        return 'Message';

      default:
        return 'Notification';
    }
  };

  
  const handleOpenCase = (notification: Notification) => {
    if (
      notification.reference_type === 'case_intake' &&
      notification.reference_id != null
    ) {
      navigate(
        `/sw-cases?case_intake_id=${encodeURIComponent(
          String(notification.reference_id)
        )}`
      );
      return;
    }

    if (notification.case_number) {
      navigate(
        `/sw-cases?case=${encodeURIComponent(
          notification.case_number
        )}`
      );
      return;
    }

    navigate('/sw-cases');
  };


  if (!user) {
    return null;
  }

  if (user.role !== 'social_worker') {
    return null;
  }

    return (
    <div className="flex h-screen bg-slate-50">
        <Sidebar />

        <main className="flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center">
                <i className="ri-notification-3-line text-2xl text-red-500" />
              </div>

              <div>
                <h1 className="text-2xl font-bold text-slate-800">
                  Notifications
                </h1>

                <p className="text-sm text-slate-500 mt-1">
                  Stay informed about urgent cases, victim reports, schedules, and case updates.
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm text-slate-600">
                {notifications.length}{' '}
                {notifications.length === 1
                ? 'notification'
                : 'notifications'}
            </span>

            {unreadCount > 0 && (
                <>
                <span className="px-3 py-2 rounded-lg bg-red-50 border border-red-100 text-sm font-semibold text-red-600">
                    {unreadCount} unread
                </span>

                <button
                    type="button"
                    onClick={markAllAsRead}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-500 text-white text-sm font-semibold hover:bg-teal-600 transition"
                >
                    <i className="ri-check-double-line" />
                    Mark All as Read
                </button>
                </>
            )}
            </div>
        </div>

        {error && (
          <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 flex items-start gap-3">
            <i className="ri-error-warning-line text-red-500 text-xl mt-0.5" />

            <div>
              <p className="text-sm font-semibold text-red-700">
                Unable to load notifications
              </p>

              <p className="text-sm text-red-600 mt-1">
                {error}
              </p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-12">
            <div className="flex flex-col items-center justify-center">
              <div className="w-10 h-10 border-4 border-slate-200 border-t-teal-500 rounded-full animate-spin" />

              <p className="mt-4 text-sm text-slate-500">
                Loading notifications...
              </p>
            </div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-12">
            <div className="flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center">
                <i className="ri-notification-off-line text-3xl text-slate-400" />
              </div>

              <h2 className="mt-5 text-lg font-semibold text-slate-700">
                No notifications
              </h2>

              <p className="mt-2 max-w-md text-sm text-slate-500">
                You currently have no notifications. Urgent cases and new victim reports will appear here.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {notifications.map(notification => {
              const unread =
                Number(notification.is_read) === 0;

              return (
                <div
                  key={notification.id}
                  className={`bg-white rounded-3xl border shadow-sm transition ${
                    unread
                      ? 'border-red-100 shadow-red-50'
                      : 'border-slate-200'
                  }`}
                >
                  <div className="p-5 sm:p-6">
                    <div className="flex items-start gap-4">

                      <div
                        className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${getNotificationColor(
                          notification.type
                        )}`}
                      >
                        <i
                          className={`${getNotificationIcon(
                            notification.type
                          )} text-xl`}
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <h2 className="text-base font-bold text-slate-800">
                                {notification.title}
                              </h2>

                              {unread && (
                                <span className="px-2 py-1 rounded-full bg-red-100 text-red-600 text-[11px] font-bold uppercase tracking-wide">
                                  New
                                </span>
                              )}
                            </div>

                            <p className="text-xs text-slate-400 mt-1">
                              {getNotificationLabel(
                                notification.type
                              )}
                            </p>
                          </div>

                          <span className="text-xs text-slate-400 whitespace-nowrap">
                            {formatDate(
                              notification.created_at
                            )}
                          </span>
                        </div>

                        <p className="text-sm text-slate-600 leading-6 mt-4">
                          {notification.message}
                        </p>

                        {notification.reference_type ===
                          'case_intake' && (
                          <div className="mt-5 flex flex-wrap items-center gap-3">
                            <button
                              type="button"
                              onClick={async () => {
                                if (
                                    Number(notification.is_read) === 0
                                ) {
                                    await markAsRead(
                                    notification.id
                                    );
                                }

                                handleOpenCase(
                                    notification
                                );
                                }}
                              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-teal-500 text-white text-sm font-semibold hover:bg-teal-600 transition"
                            >
                              <i className="ri-folder-open-line" />
                              View My Cases
                            </button>

                            <span className="text-xs text-slate-400">
                              Case reference #
                              {notification.reference_id}
                            </span>

                            {unread && (
                            <button
                                type="button"
                                onClick={() =>
                                markAsRead(
                                    notification.id
                                )
                                }
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:bg-slate-50 hover:text-teal-600 transition"
                            >
                                <i className="ri-check-line" />
                                Mark as Read
                            </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </main>
  </div>
  );
}